<script setup>
/*
	README:
	** Was initially ListBox but broken off to own component due to differences from input form listbox **

	https://headlessui.com/v1/vue/listbox

	Expected data format:
		{
			'id': Number, //ID for record
			'text': String, //Text displayed
			'disabled': Boolean, // True to disable and prevent clicking
			'class': '', // Custom CSS to add to item
			'itemIcon': Boolean || String, // Boolean: true to use item-icon slot (allows using a single SVG that can be styled to change colour)
													 // String: Src url to img
			...additional properties that will be accessible in change handler function
		}

	Emits:
		onItemSelected - Called when value changes. Passes selected item object.

	Slots:
	item-icon

	Notes:
		Set a fixed width around the listbox for truncated text to work for inline selected value

	Examples:
		Facility Types as ListBox:
			facility_types = ref([{ id: '', text: '', disabled: false, itemIcon: false, action: '' }]),
			facility_type = ref({ id: '', text: '', disabled: false, itemIcon: false, action: '' }),

		<list-box
			:inlineLabelIcon=false
			label="Facility Type"
			label-text-size="text-sm"
			:items="facility_types"
			v-model="facility_type"
			:showSelectedCheckmark="true"
			:wrapItemText = "true">
		</list-box>
 */

import { computed, defineModel } from 'vue'
import {
	Listbox,
	ListboxButton,
	ListboxOptions,
	ListboxOption,
	ListboxLabel,
} from '@headlessui/vue'
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/vue/20/solid'
import ValidationError from '@/components/Forms/InputError.vue'
import { ApiGatewayResponse } from '@/api/ApiGateway.js'

const emit = defineEmits(['onItemSelected'])

const props = defineProps({
	inlineLabel: { type: String, required: false }, //Text shown in listbox instead of selected value, omit to show selected value
	inlineLabelIcon: { type: Boolean, required: false },// True to indicate that the listbox has an inline label icon (use inline-label-icon slot for svg)
	label: { type: String, default: '' }, // Label shown above ListBox
	labelTextSize: { type: String, default: '' },// CSS Class to change size of label text
	labelClass: { type: String, default: '' },// CSS classes for additional label sizing
	items: { type: Array, default: () => [] },// Array of items (see data format defined above)
	itemClass: { type: String, default: '' },// CSS Classes to style item text
	disabled: { type: Boolean, default: false },//True to disable listbox
	showSelectedCheckmark: { type: Boolean, default: false },//True to show checkmark next to selected item
	wrapItemText: { type: Boolean, default: true }, //Set false to force listbox to grow
	errors: { type: ApiGatewayResponse, default: null },
	errorField: { type: String, default: null },
});

const labelCustomClass = computed(() =>
{
	return [props.labelClass, props.labelTextSize].join(' ')
})

const selectedItem = defineModel({ type: Object, default: { id: '', text: '', disabled: false, itemIcon: false, action: '' } })

function isItemSelected(p_item)
{
	if ((selectedItem.value === null || selectedItem.value.id === null) && (p_item.id === 0 || p_item.id === null))
	{
		return true;
	}

	return p_item.id === selectedItem.value.id
}
</script>

<template>
	<div class="flex flex-col relative">
		<Listbox v-model="selectedItem" :disabled="props.disabled" @update:modelValue="value => emit('onItemSelected', value)">
			<ListboxLabel v-if="props.label" class="leading-6 mb-3 block text-label" :class="labelCustomClass">{{ props.label }}</ListboxLabel>
			<div class="group/outer relative">
				<ListboxButton
					class="h-11 block w-full font-medium rounded bg-neutral pl-2 py-2 pr-2 text-label outline-none placeholder:text-placeholder  group-hover/outer:ring-1 group-hover/outer:ring-neutral-subtle group-hover/outer:border-neutral-subtle"
				>
					<!--focus:outline-none focus-visible:outline-indigo-500 focus-visible:ring-2 focus-visible:ring-neutral/75 focus-visible:ring-offset-2 focus-visible:ring-offset-orange-300-->
					<template #default="{ open }">
						<div class="group/inner flex flex-row justify-between w-full ">
							<div v-if="props.inlineLabelIcon" class="group-hover/outer:fill-icon-primary min-h-5 mt-[1px]">
								<slot name="inline-label-icon"></slot>
								<!--								<img :src="props.inlineLabelIcon" class="min-h-5 min-w-5 inline-block"/>-->
							</div>
							<div v-if="props.inlineLabel"
								  :class="[
									  'grow truncate text-label text-left',
									  props.inlineLabelIcon ? 'ml-4 ' : 'ml-2',
								 ]"
							>
								{{ props.inlineLabel }}
							</div>
							<div v-else class="mx-2 max-h-6 truncate">
								{{ selectedItem.text }}
							</div>
							<div v-if="!props.disabled">
								<ChevronUpIcon v-if="open" class="h-5 w-5 text-icon-neutral mt-1 group-hover/outer:fill-icon-primary-dark " aria-hidden="true" />
								<ChevronDownIcon v-else class="h-5 w-5 text-icon-neutral mt-1 group-hover/outer:fill-icon-primary-dark " aria-hidden="true" />
							</div>
						</div>
					</template>

				</ListboxButton>
				<transition
					leave-active-class="transition duration-100 ease-in"
					leave-from-class="opacity-100"
					leave-to-class="opacity-0"
				>
					<ListboxOptions
						:class="[
							'mt-1 space-y-1 ',
							]">
						<ListboxOption
							v-for="item in props.items"
							:key="item.id"
							:value="item"
							:disabled="item.disabled"
							:class="[props.itemClass, item.class ? item.class : '']"	>

							<li style="outline-width:1px" :class="[
									'group/option flex flex-row justify-between relative select-none py-2 pr-4 cursor-pointer hover:bg-primary-subtle',
									isItemSelected(item) ? 'text-primary outline-none' : 'text-label',
									props.inlineLabelIcon ? 'pl-11.5' : 'pl-4.5'
								 ]">

								<div v-if="item.itemIcon === true" class="mr-2 pt-1 max-w-5">
									<slot name="item-icon"></slot>
								</div>
								<div v-if="typeof item.itemIcon === 'string' && item.itemIcon.length > 0" class="mr-2 max-w-5">
									<img :src="item.itemIcon" alt="Item Icon" />
								</div>
								<div :class="[
									  isItemSelected(item) ? 'font-bold text-primary' : 'font-normal text-label group-hover/option:text-label',
									  'flex-1 inline-block',
									  props.wrapItemText ? '' : 'whitespace-nowrap truncate'
									]">
									{{ item.text.length === 0 ? "&nbsp;" : item.text }}<!-- nbsp needed to have blanks display full row height -->
									<div class="ml-3 text-sm font-semibold">{{ item.org_role }}</div>
								</div>
								<div
									v-show="showSelectedCheckmark && isItemSelected(item) "
								>
									<CheckIcon class="h-4 w-4 mt-1 ml-1 text-icon-neutral" aria-hidden="true" />
								</div>

							</li>

						</ListboxOption>
					</ListboxOptions>
				</transition>
			</div>
		</Listbox>
		<!-- TODO: ListBox - Test error messaging -->
		<ValidationError :errors="props.errors" :field="props.errorField" class="mt-2 ml-3.5" />
	</div>
</template>

<style scoped>

</style>